import { PermissionCode, type ArticleType } from '@gb-ticket/shared-types';
import { PlusIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';

import { ListPagination } from '@/components/list-pagination';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { adminTableFrameClassName } from '@/platform/features/admin-panel';
import { useArticleGroups, useArticles } from '@/platform/features/articles/use-articles';
import { MasterDataListState } from '@/platform/features/master-data/master-data-list-state';
import { ARTICLES_NEW_PATH, articlePath } from '@/platform/lib/article-paths';

const TYPE_LABEL: Record<ArticleType, string> = {
  material: 'Material',
  service: 'Dienstleistung',
};

const NONE = 'none';

const PAGE_SIZE = 25;

export function ArticlesPage() {
  const me = useMe();
  const canWrite = hasPermission(me.data, PermissionCode.ARTICLES_MANAGE);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [type, setType] = useState<ArticleType | ''>('');
  const [groupId, setGroupId] = useState('');
  const [stockManaged, setStockManaged] = useState<'all' | 'true' | 'false'>('all');
  const [sortBy, setSortBy] = useState<'articleNumber' | 'name'>('articleNumber');
  const groups = useArticleGroups(true);
  const query = useMemo(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      includeInactive: true,
      q: q.trim() || undefined,
      type: type || undefined,
      groupId: groupId || undefined,
      stockManaged: stockManaged === 'all' ? undefined : stockManaged === 'true',
      sortBy,
      sortOrder: 'asc' as const,
    }),
    [page, q, type, groupId, stockManaged, sortBy],
  );
  const articles = useArticles(query);
  const total = articles.data?.total ?? 0;

  return (
    <section className="space-y-6">
      <PlatformPageHeader
        title="Artikel"
        description="Material und Dienstleistungen mit Einheiten, Lieferanten und Einkaufspreisen. Inaktive Artikel bleiben in der Verwaltung sichtbar."
        action={
          canWrite ? (
            <Button type="button" asChild>
              <Link to={ARTICLES_NEW_PATH}>
                <PlusIcon className="size-4" aria-hidden />
                Artikel anlegen
              </Link>
            </Button>
          ) : undefined
        }
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="article-search">Suche</Label>
          <Input
            id="article-search"
            value={q}
            onChange={(event) => {
              setPage(1);
              setQ(event.target.value);
            }}
            placeholder="Nummer oder Bezeichnung"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="article-type">Typ</Label>
          <Select
            value={type || NONE}
            onValueChange={(value) => {
              setPage(1);
              setType(value === NONE ? '' : (value as ArticleType));
            }}
          >
            <SelectTrigger id="article-type" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Alle</SelectItem>
              <SelectItem value="material">Material</SelectItem>
              <SelectItem value="service">Dienstleistung</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="article-group">Gruppe</Label>
          <Select
            value={groupId || NONE}
            onValueChange={(value) => {
              setPage(1);
              setGroupId(value === NONE ? '' : value);
            }}
          >
            <SelectTrigger id="article-group" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Alle</SelectItem>
              {groups.data?.items.map((group) => (
                <SelectItem key={group.id} value={group.id}>
                  {group.code} · {group.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="article-stock">Lager</Label>
          <Select
            value={stockManaged}
            onValueChange={(value) => {
              setPage(1);
              setStockManaged(value as 'all' | 'true' | 'false');
            }}
          >
            <SelectTrigger id="article-stock" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle</SelectItem>
              <SelectItem value="true">Für Lager vorgesehen</SelectItem>
              <SelectItem value="false">Nicht lagergeführt</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="article-sort">Sortierung</Label>
          <Select
            value={sortBy}
            onValueChange={(value) => {
              setPage(1);
              setSortBy(value as 'articleNumber' | 'name');
            }}
          >
            <SelectTrigger id="article-sort" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="articleNumber">Nummer</SelectItem>
              <SelectItem value="name">Bezeichnung</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <MasterDataListState
        pending={articles.isPending}
        pendingLabel="Artikel werden geladen …"
        error={articles.isError ? articles.error : null}
        errorLabel="Die Artikel konnten nicht geladen werden."
        empty={articles.data?.items.length === 0}
        emptyLabel="Noch keine Artikel."
      >
        <div className={adminTableFrameClassName}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nummer</TableHead>
                <TableHead>Bezeichnung</TableHead>
                <TableHead>Typ</TableHead>
                <TableHead>Gruppe</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {articles.data?.items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-mono text-sm">{item.articleNumber}</TableCell>
                  <TableCell className="font-medium">
                    <Link to={articlePath(item.id)} className="hover:underline">
                      {item.name ?? 'Ohne Bezeichnung'}
                    </Link>
                  </TableCell>
                  <TableCell>{TYPE_LABEL[item.type]}</TableCell>
                  <TableCell>{item.groupName ?? '—'}</TableCell>
                  <TableCell>
                    {item.isActive ? (
                      <Badge variant="outline">Aktiv</Badge>
                    ) : (
                      <Badge variant="secondary">inaktiv</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <ListPagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
      </MasterDataListState>
    </section>
  );
}
